
// Packages
import React, { useState, useEffect, useCallback } from 'react';
import axios, { all } from 'axios';
import { Box, Stack, useMediaQuery, Modal, Alert, Button } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid'

// Local
import TodaysExamsTableTile from './widgets/TodaysExamsTableTile';
import AllExamsTableTile from './widgets/AllExamsTableTile';
import SingleUserTile from './widgets/SingleUserTile';
import { useGetExamComponentQuery, usePostExamComponentMutation } from '../api';
import LoadingTakeover from './buffers/LoadingTakeover';
import { useSystemMessage } from '../providers/SystemMessageContext';
import ConfirmActionModal from './modals/ConfirmActionModal';
import formatTimeZoneDateString from './utilities/formatTimeZoneDate';

const Statistics = () => {
  const [statisticsArray, setStatisticsArray] = useState([]);
  const isMobile = useMediaQuery("(max-width:600px)");
  const { showMessage } = useSystemMessage();

  const [reportOpen, setReportOpen] = useState(false);
  const toggleReportOpen = useCallback(() => {
    setReportOpen(prev => !prev);
  }, []);
  const [error, setError] = useState('');
  const [singleUserData, setSingleUserData] = useState(null);

  const { submit: gradeExamComponent, data: examComponentGraded, error: examComponentGradedError} = usePostExamComponentMutation();
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const openGradeModal = () => setGradeModalOpen(true);
  const closeGradeModal = () => setGradeModalOpen(false);
  const [gradeExams, setGradeExams] = useState(false);

  const { data: allExams, isLoading: allExamsLoading, error: allExamsError, refresh: refreshAllExams } = useGetExamComponentQuery();
  const [examRows, setExamRows] = useState([]);
  const [ungradedExpired, setUngradedExpired] = useState([]);
  
  useEffect(() => {
      if (allExams) {
          const rows = allExams.data.map((row) => ({
              ...row,
              date_taken_local: formatTimeZoneDateString(row.date_taken), 
          }));
          setExamRows(rows);
          setUngradedExpired(allExams.data.filter((exam) => {
            let diff = new Date(exam.expiration).getTime() - Date.now()
            return (exam.expired === true || diff <= 0) && (exam.graded === false) && (exam.exam_component_base__exam_component_title != 'Writing')
          }).map((exam) => {
            return exam.id
          }));
          
      } else if (allExamsError) {
          showMessage(allExamsError.response.data.message, allExamsError.response.data.success ? "success" : "error");
      }
  }, [allExams]);


  useEffect(() => {
      if (examComponentGraded) {
          showMessage(examComponentGraded.message, examComponentGraded.success ? "success" : "error");
          closeGradeModal();
          refreshAllExams();
      } else if (examComponentGradedError) {
          showMessage(examComponentGradedError.response.data.message, examComponentGradedError.response.data.success ? "success" : "error");
      }
  }, [examComponentGraded, examComponentGradedError]);

  useEffect(() => {
      if (!gradeExams || ungradedExpired?.length === 0) return;
      handleGradeExams(ungradedExpired);
      setGradeExams(false);
  }, [gradeExams]);


  const handleGradeExams = async (examComponentIds) => {
      await gradeExamComponent({
          "grade": {
              "exam_component_ids": examComponentIds
          }
      });
  };



  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '65vw',
    minWidth:'45vw',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
    display:'flex',
    flexDirection:'column',
    [`.${gridClasses.cell}.fail`]: {
      backgroundColor: '#FF9494',
      color: '#000000',
    },
    [`.${gridClasses.cell}.pass`]: {
      backgroundColor: '#A3FFAE',
      color: '#000000',
    },
    [`.${gridClasses.cell}.null`]: {
      backgroundColor: '#EBEBEB',
      color: '#000000',
    },
  }

  const mobileModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: '65vw',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
    display:'flex',
    flexDirection:'column'
  }

  useEffect(() => {
    const token = localStorage.getItem("access_token");

    if (token) {
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/exams/retrieve_statistics_table/`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((response) => {
        setStatisticsArray(response.data.statistics_array);
      })
      .catch((error) => {
        console.error("Error fetching statistics:", error);
      });
    }
  }, []);

  const [reportOpenSwitch, setReportOpenSwitch] = useState(false);
  const handleRowClickAllExamsTable = (params) => {
    setSingleUserData(params.row.test_taker__id);
    setReportOpenSwitch(!reportOpenSwitch);
  }

  useEffect(() => {
    if (!singleUserData) {
      return;
    } else {
      toggleReportOpen();
    }
  }, [reportOpenSwitch, toggleReportOpen])
  
  return (
    <Box
      sx={{
        height: '100%',
        width: '80%',
        [`.${gridClasses.cell}.fail`]: {
          backgroundColor: '#FF9494',
          color: '#000000',
        },
        [`.${gridClasses.cell}.pass`]: {
          backgroundColor: '#A3FFAE',
          color: '#000000',
        },
        [`.${gridClasses.cell}.null`]: {
          backgroundColor: '#EBEBEB',
          color: '#000000',
        },
        display:'flex',
        flexDirection:'column',
      }}
    >
      <Modal
        open={reportOpen}
        onClose={toggleReportOpen}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <SingleUserTile user_data={singleUserData} modalView={true}/>
      </Modal>
      <Modal
          open={gradeModalOpen}
          onClose={closeGradeModal}
      >
          <ConfirmActionModal
              actionString={'grade your exam. You will not be able to re-enter the exam once it is graded'}
              confirmationHook={setGradeExams}
              handleClose={closeGradeModal}
          />
      </Modal>
      {error && (<Alert severity='error' sx={{mt:1, mb:1}} onClose={() => {setError('')}}>{error}</Alert>)}
      { allExamsLoading ? (<LoadingTakeover />) : (
        <Stack spacing={6} sx={{ mb:4, }}>
          <TodaysExamsTableTile rows={examRows} onRowClick={handleRowClickAllExamsTable} />
          <AllExamsTableTile rows={examRows} onRowClick={handleRowClickAllExamsTable} />
          { ungradedExpired.length > 0 && (
            <Button variant='contained' onClick={openGradeModal}>Grade Expired Exams ({ungradedExpired.length})</Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default Statistics;